import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import firebase from 'firebase/app';
import 'firebase/auth';
import UserCredential = firebase.auth.UserCredential;
import { IRegisterUser } from '../../users/interfaces/user.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LocalStorageKeys } from '../local-storage/local-storage.enum';
import { FirestoreRulesService } from '../firestore-rules/firestore-rules.service';
import { ToastrService } from 'ngx-toastr';
import { UserAdminVerificationStatusEnum } from 'src/app/users/interfaces/user-values';

@Injectable()
export class AuthService {

  userData: any;

  constructor(
    private localStorageService: LocalStorageService,
    private afAuth: AngularFireAuth,
    private readonly firestoreRulesService: FirestoreRulesService,
    private toastr: ToastrService,
  ) {

    /* Saving user data in localstorage when
    logged in and setting up null when logged out */
    this.afAuth.authState.subscribe(user => {
      this.stampUser(user);
    });
  }

  signInWithGoogle(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const googleProvider = new firebase.auth.GoogleAuthProvider();
      firebase.auth().signInWithPopup(googleProvider)
        .then((user: UserCredential) => {
          if (user) {
            this.stampUser(user.user);
            if (user.additionalUserInfo.isNewUser) {
              this.addUserToRoles(user?.user?.uid);
              const names: Array<string> = user.user.displayName.split(' ');
              this.initializeUserMetaData(user?.user?.uid, names[0], names.length > 1 ? names[1] : '', user.user.email);
            }
            resolve(user);
          }
        }).catch(error => {
          console.error('error', error.message);
          reject(error);
        });
    });
  }

  signInWithFacebook(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const facebookProvider = new firebase.auth.FacebookAuthProvider();
      firebase.auth().signInWithPopup(facebookProvider)
        .then((user: UserCredential) => {
          if (user) {
            this.stampUser(user.user);
            if (user.additionalUserInfo.isNewUser) {
              this.addUserToRoles(user?.user?.uid);
              const names = user.user.displayName.split(' ');
              this.initializeUserMetaData(user?.user?.uid, names[0], names.length > 1 ? names[1] : '', user.user.email);
              this.sendVerificationMail();
            }
            resolve(user);
          }
        })
        .catch(error => {
          console.error('error', error.message);
          reject(error);
        });
    });
  }

  deleteAccount = () => {
    firebase.auth().currentUser.delete()
      .then(() => {
        this.clearLocalStorageByKey([LocalStorageKeys.token,
        LocalStorageKeys.emailVerificationSent,
        LocalStorageKeys.isAuthenticatedWithEmail,
        LocalStorageKeys.currentRole]);
      })
      .catch(error => {
        if (error.code === 'auth/requires-recent-login') {
          firebase.auth().signOut()
            .then(() => {
              // todo navigate to register page
              // showSignInForm()
              // displayMessage(`error`, error.message);
              console.error('error', error.message);
            });
        }
      });
  }

  forgotPassword(data: { email: string }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      firebase.auth().sendPasswordResetEmail(data.email)
        .then(() => {
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  registerAndUpdate(userData: IRegisterUser): Promise<UserCredential> {
    return new Promise<any>((resolve, reject) => {
      this.register(userData)
        .then((user) => {
          this.setUserDisplayName(userData.firstName, userData.lastName).then((updatedUser) => {
            // get new user object
            user = firebase.auth().currentUser;
            this.localStorageService.write(LocalStorageKeys.isAuthenticatedWithEmail, 'true');
            resolve(user);
          });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  // TODO avraham : fix the interface difference, in running time the interface IFireBaseUser shape is returned in the object
  // it looks like the unterfaec IUser is imcomplete
  getCurrentUser(): Promise<any |  null> {
    return new Promise<any>((resolve, reject) => {
      const currentUser = JSON.parse(this.localStorageService.read(LocalStorageKeys.currentUser));
      if (currentUser) {
        resolve(currentUser);
      } else {
        resolve(null);
      }
    });
  }

  private async register(userData: IRegisterUser): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {
      try {
        await this.afAuth.createUserWithEmailAndPassword(userData.email, userData.password)
          .then((user: any) => {
            // If a user is successfully created with an appropriate email
            if (user) {
              this.addUserToRoles(user?.user?.uid);
              this.initializeUserMetaData(user?.user?.uid, userData.firstName, userData.lastName, userData.email);
              this.sendVerificationMail();
              resolve(user);
            }
          }).catch((error) => {
            reject(error);
          });
      } catch (err) {
        throw err;
      }
    });
  }

  private addUserToRoles(uid: string): void {
    if (!uid) {
      console.error(`Invalid uid - undefined`);
      return;
    }
    // write uid in rolesToUsers
    this.firestoreRulesService.addUserToRoles(uid);
  }

  private initializeUserMetaData(uid: string, firstName: string, lastName: string, email: string): void {
    if (!uid) {
      console.warn(`Invalid uid - undefined`);
      return;
    }
    // write uid in rolesToUsers
    const initMetadata = {firstName,
                          lastName,
                          email,
                          adminVerificationStatus: UserAdminVerificationStatusEnum.PENDING,
                          updated_at: new Date()};
    this.firestoreRulesService.initializeUserMetadata(uid, initMetadata);
    this.localStorageService.write(LocalStorageKeys.userMetadata, initMetadata);
  }

  setUserDisplayName(firstName: string, lastName: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      firebase.auth().currentUser.updateProfile({ displayName: firstName + ' ' + lastName }).then(() => {
        const user = firebase.auth().currentUser;
        this.stampUser(user);
        resolve(user);
      }).catch((err) => {
        reject(err);
      });
    });
  }

  signIn(userData: IRegisterUser): Promise<UserCredential> {
    return this.afAuth.signInWithEmailAndPassword(userData.email, userData.password);
  }

  signInAndUpdate(userData: IRegisterUser): Promise<UserCredential> {
    return new Promise<any>((resolve, reject) => {
      this.clearLocalStorageByKey([LocalStorageKeys.token,
        LocalStorageKeys.currentUser,
        LocalStorageKeys.emailVerificationSent,
        LocalStorageKeys.isAuthenticatedWithEmail,
        LocalStorageKeys.currentRole,
        LocalStorageKeys.userMetadata]);
      this.signIn(userData)
        .then((user) => {
          this.localStorageService.write(LocalStorageKeys.isAuthenticatedWithEmail, 'true');
          this.stampUser(user);
          resolve(user);
        })
        .catch((error) => {
          console.error('error ', error);
          reject(error);
        });
    });
  }

  logout(): void {
    firebase.auth().signOut();
    this.clearLocalStorageByKey([LocalStorageKeys.token,
    LocalStorageKeys.currentUser,
    LocalStorageKeys.emailVerificationSent,
    LocalStorageKeys.isAuthenticatedWithEmail,
    LocalStorageKeys.currentRole,
    LocalStorageKeys.userMetadata
    ]);
  }

  private clearLocalStorageByKey(keys: LocalStorageKeys[]): void {
    keys.forEach((key: LocalStorageKeys) => {
      this.localStorageService.clear(key);
    });
  }

  private stampUser(user: any): void {
    if ( window !== window.parent ) {
      return; // do nothing
    }
    if (user) {
      this.userData = user;
      this.localStorageService.write(LocalStorageKeys.currentUser, JSON.stringify(this.userData));
      this.localStorageService.write(LocalStorageKeys.uid, this.userData.uid);
      try {
        this.userData?.getIdToken().then(
          (token: string) => {
            this.localStorageService.write(LocalStorageKeys.token, token);
          }
        );
      } catch (err) {
        console.error(err);
      }
    } else {
      this.clearLocalStorageByKey([LocalStorageKeys.token,
      LocalStorageKeys.currentUser,
      LocalStorageKeys.emailVerificationSent,
      LocalStorageKeys.isAuthenticatedWithEmail,
      LocalStorageKeys.currentRole,
      LocalStorageKeys.userMetadata
    ]);
    }
  }

  sendVerificationMail(): void {
    firebase.auth().currentUser.sendEmailVerification().then(() => {
      this.toastr.info('Verification mail was sent', 'Check your mail!');
    }).catch((error) => {
      this.toastr.error('Verification mail failed to send', 'Error!');
      console.error('Error: ', error);
    });
  }

  getCurrentUserToken(user?: any): void {
    const currentUser = user ? user : firebase.auth().currentUser;
    currentUser.getIdToken().then(
      (token: string) => {
        this.localStorageService.write(LocalStorageKeys.token, token);
        this.localStorageService.write(LocalStorageKeys.currentUser, JSON.stringify(currentUser));
      }
    );

    this.localStorageService.read(LocalStorageKeys.token);
  }

  isAuthenticated(): boolean {
    return !!this.localStorageService.read(LocalStorageKeys.token);
  }

  isMailVerified(): boolean {
    const currentUser = JSON.parse(this.localStorageService.read(LocalStorageKeys.currentUser));
    return !!currentUser.emailVerified;
  }
}
